import ReelCascadeBehaviour from "../../../vendor_core/core/src/spinners/ReelCascadeBehaviour";
import BURJackpotsModel from "../models/BURJackpotsModel";
import BURReelSymbol from "../view/BURReelSymbol";

class BURReelCascadeBehaviour extends ReelCascadeBehaviour {
    private currentSymbolIndex: number;
    public get startSymbolsTweenPosition(): number {
        const symbolsHeight = this.reel.symbols
            .slice(this.currentSymbolIndex, this.reel.symbols.length)
            .reduce((acc, obj) => acc + obj.height * 2, 0);
        return this.reel.yOrigin - symbolsHeight;
    }
    public get endSymbolsTweenPosition(): number {
        const allSymbolsHeight = this.reel.symbols.reduce(
            (acc, obj) => acc + obj.height,
            500
        );

        const symbolsHeight = this.reel.symbols
            .slice(0, this.currentSymbolIndex)
            .reduce((acc, obj) => acc + obj.height, 0);
        return this.reel.yOrigin + allSymbolsHeight + symbolsHeight;
    }
    public spin() {
        super.spin();
    }

    protected stopMainDrop() {
        this.cascadeTL?.clear();
        const startSym = this.startSymbolIndex;
        let symIdx = this.startSymbolIndex;
        const jackpotFragments = BURJackpotsModel.jackpot_fragments.filter(
            (f) => f.x === this.reel.reelIndex
        );

        // Set the final symbols first, because the start position is calculated from the symbols height,
        // which depends on the symbol. This way it is calculated once for the whole reel.
        while (symIdx >= 0 && symIdx < this.reel.symbols.length) {
            const sym = this.reel.symbols[symIdx] as BURReelSymbol;
            if (!sym.locked) {
                const jpFragment = jackpotFragments.find((f) => f.y === symIdx);
                // If there is a jackpot fragment on the current symbol position,
                // don't set the final symbol id yet. It will be set in the jackpots progress state.
                if (jpFragment) {
                    sym.setJackpotFragment(jpFragment.tier);
                } else {
                    sym.setId(this.stopSymbols[symIdx]);
                }
            }
            symIdx = this.getNextSymbolIndex(symIdx);
        }
        const startY = this.startSymbolsTweenPosition;

        // Loop the symbols from the start symbol till the other end of the reel
        // Depends on the getNextSymbolIndex function to count in the correct direction
        symIdx = this.startSymbolIndex;
        while (symIdx >= 0 && symIdx < this.reel.symbols.length) {
            let targetDelay = this.getTargetDelayInMainSpin();
            let sym = this.reel.symbols[symIdx] as BURReelSymbol;
            const targetY = this.symbolsDefaultPosition[symIdx];
            if (!sym.locked) {
                sym.y = startY;
                if (symIdx != startSym) {
                    this.cascadeTL?.to(
                        sym,
                        this.stopSymbolAnimTime,
                        {
                            y: targetY,
                            ease: this.mainEasing,
                            onComplete: () => {
                                this.symCascadeComplete(false);
                            },
                            callbackScope: this
                        },
                        "<" + targetDelay
                    );
                } else {
                    this.cascadeTL?.to(
                        sym,
                        this.stopFirstSymbolAnimTime,
                        {
                            delay: this.animationStartDelay,
                            y: targetY,
                            ease: this.mainEasing,
                            onComplete: () => {
                                this.symCascadeComplete(false);
                            },
                            callbackScope: this
                        },
                        "<" + targetDelay
                    );
                }
            } else {
                sym.setIsLocked(false);
            }
            symIdx = this.getNextSymbolIndex(symIdx);
        }
        this.cascadeTL?.restart();
    }

    protected spinMainDrop() {
        this.cascadeTL?.clear();
        const startSym = this.startSymbolIndex;
        let symIdx = this.startSymbolIndex;
        // The symbols are not changed in the loop, so the end position is the same for all of them
        const targetY = this.endSymbolsTweenPosition;

        // Loop the symbols from the start symbol till the other end of the reel
        // Depends on the getNextSymbolIndex function to count in the correct direction
        while (symIdx >= 0 && symIdx < this.reel.symbols.length) {
            let targetDelay = this.getTargetDelayInMainSpin();
            let sym = this.reel.symbols[symIdx] as BURReelSymbol;

            if (!sym.locked) {
                if (symIdx != startSym) {
                    this.cascadeTL?.to(
                        sym,
                        this.spinSymbolAnimTime,
                        {
                            y: targetY,
                            ease: this.mainEasing,
                            onComplete: () => {
                                this.symCascadeComplete(false);
                            },
                            callbackScope: this
                        },
                        "<" + targetDelay
                    );
                } else {
                    this.cascadeTL?.to(
                        sym,
                        this.spinFirstSymbolAnimTime,
                        {
                            delay: this.animationStartDelay,
                            y: targetY,
                            ease: this.mainEasing,
                            onComplete: () => {
                                this.symCascadeComplete(false);
                            },
                            callbackScope: this
                        },
                        "<" + targetDelay
                    );
                }
            }

            symIdx = this.getNextSymbolIndex(symIdx);
        }
        this.cascadeTL?.restart();
    }

    protected setFinalSymbols() {
        const jackpotFragments = BURJackpotsModel.jackpot_fragments.filter(
            (f) => f.x === this.reel.reelIndex
        );
        this.reel.sortSymbolsByY();
        this.stopSymbols.forEach((sym, index) => {
            const jpFragment = jackpotFragments.find((f) => f.y === index);
            // If there is a jackpot fragment on the current symbol position,
            // don't set the final symbol id yet. It will be set in the jackpots progress state.
            if (!jpFragment) {
                this.reel.symbols[index].setId(sym);
            }
        });
    }
}

export default BURReelCascadeBehaviour;
